import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import ItemForm, { itemPayload } from "../../components/items/ItemForm";
import { emptySubItem } from "../../components/items/SubItemsForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearCurrentItem,
  fetchItemCategoriesData,
  fetchItemDetail,
  fetchItemMaterialsData,
  fetchItemSizesData,
  getCurrentItem,
  getCurrentItemLoading,
} from "../../features/items/itemsSlice";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import {
  inheritedSizeRows,
  sizeFormRows,
  uniqueSizeNames,
} from "../../static/itemSize";
import { rateText } from "../../static/processRate";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IFormInput } from "../../types/item";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "આઈટમ્સ", href: route.items },
  { label: "આઈટમ સુધારો" },
];

const ItemEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentItem = useAppSelector(getCurrentItem);
  const currentItemLoading = useAppSelector(getCurrentItemLoading);
  const sizeUnit = useAppSelector(getSizeUnit);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchItemDetail(Number(id)));
      dispatch(fetchItemCategoriesData());
      dispatch(fetchItemMaterialsData());
      dispatch(fetchItemSizesData());
    }
    return () => {
      dispatch(clearCurrentItem());
    };
  }, [id, dispatch]);

  const handleGoBack = () => {
    navigate(route.items);
  };

  const handleSubmit = async (data: IFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/items/${id}`, itemPayload(data))
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.items);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo((): IFormInput | undefined => {
    if (!currentItem) return undefined;

    const subItems = currentItem.sub_items || [];
    const ownSizes = sizeFormRows(currentItem.sizes);
    // An item with sub items picks the sizes for all of them. Sub items used to pick their own, so it starts with every
    // size that it or any sub item has, and nothing is lost. Its own values stay, so the page can warn that saving
    // drops them.
    const sizes =
      subItems.length > 0
        ? inheritedSizeRows(
            uniqueSizeNames(
              [
                ...ownSizes,
                ...subItems.flatMap((subItem) => sizeFormRows(subItem.sizes)),
              ].map((size) => size.name),
              sizeUnit
            ).map((name) => ({ name })),
            ownSizes,
            {},
            sizeUnit
          )
        : ownSizes;

    return {
      category_id: currentItem.category_id,
      name: currentItem.name,
      description: currentItem.description || "",
      // Send a saved photo back only while its file still exists.
      image: currentItem.image_url ? currentItem.image : null,
      image_url: currentItem.image_url,
      has_sub_items: subItems.length > 0,
      material_id: currentItem.material_id,
      cutting_rate: rateText(currentItem.cutting_rate),
      turning_rate: rateText(currentItem.turning_rate),
      vmc_rate: rateText(currentItem.vmc_rate),
      cut_length: rateText(currentItem.cut_length),
      sizes,
      sub_items:
        subItems.length > 0
          ? subItems.map((subItem) => ({
              sub_item_id: subItem.id,
              saved_code: subItem.name,
              code: null,
              description: subItem.description || "",
              image: subItem.image_url ? subItem.image : null,
              image_url: subItem.image_url,
              pieces: String(subItem.pieces),
              material_id: subItem.material_id,
              cutting_rate: rateText(subItem.cutting_rate),
              turning_rate: rateText(subItem.turning_rate),
              vmc_rate: rateText(subItem.vmc_rate),
              cut_length: rateText(subItem.cut_length),
              sizes: inheritedSizeRows(
                sizes,
                sizeFormRows(subItem.sizes),
                {},
                sizeUnit
              ),
            }))
          : [{ ...emptySubItem }],
    };
  }, [currentItem, sizeUnit]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>આઈટમ સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentItemLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentItem && defaultValues ? (
          <ItemForm
            key={currentItem.id}
            defaultValues={defaultValues}
            savedName={currentItem.name}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={handleGoBack}
            isEdit
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            આઈટમ ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default ItemEditPage;
